/* eslint-disable react/jsx-pascal-case */
import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { BaseURL, fetchTimeout } from "../../components/Common";
import * as BSL_Common from "../../components/BSL_CommonControls";
import { Palette, API_BASE } from "../../AppConfig";
import BSL_TextInput from "../../components/BSL_TextInput";
import BSL_Button from "../../components/BSL_Button";
import BSL_Select from "../../components/BSL_Select";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import Message from "../../components/Message";
import Draggable from "react-draggable";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import FrameAppBar from "./FrameAppBar";
import { SHOW_DESIGNER_MESSAGE } from "../../redux_store/appState";
import { updateJobParameter } from "../../redux_store/jobState";
import { IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import { addStatusTrackingEntry } from "../../helper/Helper";

const useStyles = makeStyles({
    root: {
        position: "fixed",
        width: "40%",
        minWidth: "500px",
        zIndex: "11000",
        background: "#e2e9efe6",
        bottom: "20px",
        left: "101px",
        border: "1px solid grey",
        backdropFilter: "blur(4px)",
        borderRadius: "3px",
        display: "flex",
        flexDirection: "column",
        padding: "10px",
    },
    attachmentLabel: {
        alignSelf: "center",
        fontSize: "10pt",
        flex: 1,
        textAlign: "start",
        display: "flex",
        flexDirection: "column",
    },
    attachmentItem: {
        background: "#fffff3",
        borderRadius: "14px",
        padding: "0px 10px",
        whiteSpace: "nowrap",
        margin: "2px",
        display: "flex",
        alignItems: "center",
    },
    deleteButton: {
        fontSize: "0.5rem",
        padding: "0px",
    },
});

function bytesToSize(bytes) {
    var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes == 0) return "0 Byte";
    var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
}

const ORDER_REVIEW = "Order Review";
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;

const buildDisplayName = (u) => {
    if (!u) return "";
    if (u.DisplayName) return u.DisplayName;

    const first = u.FirstName ?? "";
    const last = u.LastName ? ` ${u.LastName}` : "";
    return `${first}${last}`.trim();
};

const formatTo = (name, email) => {
    const safeName = (name ?? "").replaceAll('"', '\\"');
    const safeEmail = (email ?? "").trim();
    return safeEmail ? `"${safeName}" <${safeEmail}>` : "";
};

export default function MessageSupport({
    mode,
    messageType,
    subject,
    defaultMessage,
    setShowMessageSupport,
    onSendSuccess,
    onSendError,
    style,
}) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const inputRef = useRef(null);

    const user = useSelector((state) => state?.app?.user);
    const job = useSelector((state) => state?.job);
    const config = useSelector((state) => state?.config);

    const [attachedFileForUpload, setAttachedFileForUpload] = useState([]);
    const [sending, setSending] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [messageText, setMessageText] = useState(defaultMessage ?? "");
    const [to, setTo] = useState(() => {
        if (mode === ORDER_REVIEW) {
            return formatTo(buildDisplayName(job?.User), job?.User?.Email);
        }

        const email = window.debug ? "centsupport@qikquotecentenary.com" : config?.region?.support_email;

        return formatTo(config?.region?.name, email);
    });

    if (!job?.id) return null;

    const regionName = config?.region?.name ?? "";
    const supportEmail = window.debug ? "centsupport@qikquotecentenary.com" : config?.region?.support_email;
    const jobName = job?.Customer?.JobName ?? "";
    const baseJobSubject =
        messageType === "FREIGHT POA"
            ? `Insulated Panel Delivery Charge Request for #${job.id} (${jobName})`
            : `Support Request for #${job.id} (${jobName})`;

    const orderReviewSubject = `Order Review for #${job.id} (${jobName})`;
    const isOrderReview = mode === ORDER_REVIEW;
    const bg_colour = isOrderReview
        ? Palette.FieldersOrange
        : window.LYSAGHT
            ? Palette.Lysaght.DarkBlue
            : Palette.FieldersBlue;

    const title = isOrderReview ? "Send Order Review" : `Request Support from ${regionName}`;
    const send_button_text = isOrderReview ? "SEND ORDER REVIEW" : "SEND REQUEST";
    const cc = isOrderReview ? formatTo(regionName, supportEmail) : formatTo(buildDisplayName(user), user?.Email);
    const messageSubject = subject ?? (isOrderReview ? orderReviewSubject : baseJobSubject);

    const sendRequest = async () => {
        if ((messageText ?? "").trim().length < 4) {
            setShowMessage({
                type: "warning",
                message: "Please write a short message to explain your support request.",
            });
            return;
        }

        setSending(true);

        try {
            const apiUrl = `${API_BASE()}Message/`;

            const attachments = Array.isArray(attachedFileForUpload) ? [...attachedFileForUpload] : [];
            let attachmentFilenamesHtml = "";

            const inlineAttachments = [];
            try {
                const dataUrl = window.paper_canvas?.toDataURL?.();
                if (dataUrl) {
                    const [meta, base64Data] = dataUrl.split(",");
                    const mimeType = meta?.match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/)?.[0] ?? "image/png";

                    inlineAttachments.push({
                        "Content-type": mimeType,
                        Filename: `${job?.id}.png`,
                        size: base64Data?.length ?? 0,
                        content: base64Data ?? "",
                    });
                }
            } catch { }

            if (attachments.length > 0) {
                let totalBytes = 0;

                for (const item of attachments) {
                    const name = item?.Filename ?? "Unknown";
                    const size = Number(item?.size ?? 0);

                    attachmentFilenamesHtml +=
                        (attachmentFilenamesHtml ? "<br />" : "") + `${name} (${bytesToSize(size)})`;

                    totalBytes += size;
                }

                if (totalBytes > MAX_TOTAL_BYTES) {
                    setShowMessage({
                        type: "error",
                        message: `Error: Attached files are too large. Max size overall = ${bytesToSize(
                            MAX_TOTAL_BYTES
                        )}`,
                    });
                    return;
                }

                if (totalBytes > MAX_TOTAL_BYTES / 2) {
                    setShowMessage({ type: "warning", message: "uploading attachments..." });
                }
            }

            const baseTemplate = mode === ORDER_REVIEW ? getOrderReviewTemplate() : getSupportRequestTemplate();
            const messageHtml = (messageText ?? "").replace(/(?:\r\n|\r|\n)/g, "<br>");

            let html = `
                <div style="font-size:16px;color:#01507e;">
                    ${messageHtml}
                </div>
                <br />
                ${baseTemplate}
            `;

            const fullName = buildDisplayName(user);
            const jobName = job?.Customer?.JobName ?? "";
            const jobId = job?.id ?? "";

            html = html.replace("$$_USERNAME_$$", fullName ?? "");
            html = html.replace("$$_PHONE_$$", user?.Phone ?? "");
            html = html.replace("$$_EMAIL_$$", user?.Email ?? "");
            html = html.replace("$$_BUSINESS_$$", user?.BusinessDetails?.Name ?? "");
            html = html.replace("$$_JOB_ID_$$", `${jobId}${jobName ? ` (${jobName})` : ""}`);
            html = html.replace("$$_MESSAGE_$$", messageHtml);
            html = html.replace("$$_ATTACHMENTS_$$", attachmentFilenamesHtml || "None");
            html = html.replaceAll("$$_JOB_URL_$$", `${window.location.origin}${BaseURL()}?job=${jobId}`);
            html = html.replace(
                "$$_DESIGN_SUMMARY_$$",
                `${job?.DesignSummary ?? ""}<br />
                <img src="cid:${jobId}.png" width="100%" style="background:white; width:100%; max-width:900px;">`
            );

            const payload = {
                FromEmail: window.LYSAGHT ? "no_reply@lysaght.com" : "no_reply@qikquotecentenary.com",
                FromName: window.LYSAGHT ? "Lysaght Living Designer (No Reply)" : "Centenary Design Suite (No Reply)",
                Subject: messageSubject,
                TextPart: messageText,
                HtmlPart: html,
                To: to,
                CC: cc,
                Attachments: attachments,
                InlineAttachments: inlineAttachments,
                job: job,
            };

            const response = await fetchTimeout(
                apiUrl,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
                60000
            );

            if (response.ok) {
                const msg = { type: "Success", message: "Success: Emails sent" };
                setShowMessage(msg);
                dispatch({ type: SHOW_DESIGNER_MESSAGE, message: msg });

                const messageTextSnapshot = messageText;
                setMessageText("");
                setShowMessageSupport(false);

                if (typeof onSendSuccess === "function") {
                    onSendSuccess({ to, content: messageText });
                }

                if (mode === ORDER_REVIEW) {
                    dispatch(
                        updateJobParameter(null, {
                            OrderReviewMessage: messageTextSnapshot,
                            OrderStatus: "Reviewed",

                        })
                    );
                }
            } else {
                const errorMessage = (await response.text()) || `Request failed (${response.status})`;

                if (typeof onSendError === "function") {
                    onSendError({ to: to, content: messageText, msg: errorMessage });
                }

                setShowMessage({
                    type: "error",
                    message: "Error sending support request. Please try again.",
                });
            }
        } catch (e) {
            if (typeof onSendError === "function") {
                onSendError({ to: to, content: messageText, msg: e?.message ?? "Unknown error" });
            }

            setShowMessage({
                type: "error",
                message: "Possible error sending support request. Please check your email to see if it was sent.",
            });
        } finally {
            setSending(false);
        }
    };

    const uploadFile = (e) => {
        const input = e.target;
        const file = input?.files?.[0];
        if (!file) return;

        if (file.size > MAX_TOTAL_BYTES) {
            setShowMessage({
                type: "warning",
                message: `Error: File is too large to attach. Max size = ${bytesToSize(MAX_TOTAL_BYTES)}`,
            });
            input.value = "";
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string") return;

            const [meta = "", base64Data = ""] = result.split(",");

            const mimeType =
                meta.match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/i)?.[0] || file.type || "application/octet-stream";

            setAttachedFileForUpload((prev) => [
                ...(Array.isArray(prev) ? prev : []),
                {
                    "Content-type": mimeType,
                    Filename: file.name,
                    size: file.size,
                    content: base64Data,
                },
            ]);

            input.value = "";
        };

        reader.onerror = () => {
            setShowMessage({
                type: "error",
                message: "Error reading file. Please try again.",
            });
            input.value = "";
        };

        reader.readAsDataURL(file);
    };

    const deleteAttachment = (index) => {
        let newattachedFileForUpload = [...attachedFileForUpload];
        newattachedFileForUpload.splice(index, 1);
        setAttachedFileForUpload(newattachedFileForUpload);
    };

    return (
        <Draggable handle="#handle">
            <div className={classes.root} style={style}>
                <span id="handle" style={{ cursor: "grab" }}>
                    <FrameAppBar
                        title={
                            <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
                                <DragIndicatorIcon style={{ alignSelf: "center" }} />
                                <span style={{ flex: 1 }}>{title}</span>
                            </div>
                        }
                        style={{ background: bg_colour }}
                        onClick={() => setShowMessageSupport(false)}
                    />
                </span>
                <BSL_Common.SectionSubHeader>
                    {user.Role >= 90 && (
                        <BSL_Common.SectionRow
                            style={{
                                alignItems: "center",
                            }}
                        >
                            <span
                                style={{
                                    font: '12pt "Univers45", sans-serif',
                                }}
                            >
                                <strong>to:</strong>
                            </span>{" "}
                            <BSL_Select
                                value={to}
                                tight={true}
                                items={
                                    user.Role >= 90
                                        ? [
                                            to,
                                            window.brand === "Lysaght"
                                                ? '"BBC Tech Support" <bbcsoftwaresupport@bluescopesteel.com>'
                                                : '"Tech Support" <centsupport@qikquotecentenary.com>',
                                        ]
                                        : [to, '"BBC Tech Support" <bbcsoftwaresupport@bluescopesteel.com>']
                                }
                                onChange={(e, v) => {
                                    setTo(e.target.value);
                                }}
                            />
                        </BSL_Common.SectionRow>
                    )}
                    {user.Role <= 90 && (
                        <>
                            <strong>to:</strong> {to}
                            <br />
                        </>
                    )}
                    <strong>cc:</strong> {cc}
                    <br />
                    <strong>subject:</strong> {messageSubject}
                </BSL_Common.SectionSubHeader>
                <BSL_TextInput
                    multiline
                    tight
                    value={messageText}
                    rows={8}
                    style={{
                        height: "200px",
                        flex: 1,
                    }}
                    onBlur={(val) => {
                        setMessageText(val);
                    }}
                />

                <BSL_Common.SectionRow>
                    <input
                        ref={inputRef}
                        key={"input_x"}
                        accept=".pdf, .png, .jpg, .jpeg, .gif, .doc, .docx, .xls, .xlsx"
                        style={{ display: "none" }}
                        id="support_attachment"
                        multiple
                        type="file"
                        onChange={uploadFile}
                    />
                    <label htmlFor="support_attachment">
                        <BSL_Button
                            secondary
                            variant="contained"
                            component="span"
                            label={<AttachFileIcon />}
                            style={{
                                margin: "10px",
                            }}
                        />
                    </label>
                    <div className={classes.attachmentLabel}>
                        {attachedFileForUpload.map((item, index) => {
                            return (
                                <div className={classes.attachmentItem}>
                                    <span style={{ flex: 1 }}>
                                        {item.Filename + " (" + bytesToSize(item.size) + ")"}
                                    </span>
                                    <IconButton
                                        className={classes.deleteButton}
                                        onClick={() => deleteAttachment(index)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                            );
                        })}
                    </div>
                    <BSL_Button
                        variant="contained"
                        component="span"
                        label={sending ? <CircularProgress /> : send_button_text}
                        style={{
                            margin: "10px",
                            maxHeight: "42px",
                        }}
                        disabled={sending}
                        onClick={sendRequest}
                    />
                </BSL_Common.SectionRow>
                <Message
                    open={showMessage}
                    message={showMessage?.message}
                    type={showMessage?.type}
                    onClose={() => setShowMessage(null)}
                />
            </div>
        </Draggable>
    );
}

const getSupportRequestTemplate = () => {
    return `<h2 style="color: #2e6c80;"><span style="color: #000080;">Support Request Details</span></h2>
<p>A Support Request has been made by</p>
<table style="border-collapse: collapse; width: 100%" border="1" cellpadding="5px">
<tbody>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">USER NAME</td>
<td style="font-size: 10pt;">$$_USERNAME_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">PHONE / EMAIL</td>
<td style="font-size: 10pt;">$$_PHONE_$$ / $$_EMAIL_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">BUSINESS</td>
<td style="font-size: 10pt;">$$_BUSINESS_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">JOB ID / LINK</td>
<td style="font-size: 10pt;">$$_JOB_ID_$$<br /><a href="$$_JOB_URL_$$" target="_blank">$$_JOB_URL_$$</a></td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">DESIGN TYPE</td>
<td style="font-size: 10pt;">$$_DESIGN_SUMMARY_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">MESSAGE</td>
<td style="font-size: 10pt;">$$_MESSAGE_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: rgba(221, 229, 236, 0.86);">ATTACHMENTS</td>
<td style="font-size: 10pt;">$$_ATTACHMENTS_$$</td>
</tr>
</tbody>
</table>
<p><span style="color: #999999;"><em>This email was sent from a no-reply email address, however this email has been CC'd to the requester, so you may "Reply All' to communicate directly via email with the requester.</em></span></p>`;
};

const getOrderReviewTemplate = () => {
    return `<h2 style="color: #2e6c80;"><span style="color: #806020;">${
        window.LYSAGHT ? "Lysaght Living Designer" : "Centenary Design Suite"
        } - Order Review Details</span></h2>
<p>A review has been made to your order: please make any necessary changes or contact ${
        window.LYSAGHT ? "Lysaght" : "Fielders"
        } for clarification.</p>
<table style="border-collapse: collapse; width: 100%" border="1" cellpadding="5px">
<tbody>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: #f6e6c5db;">JOB ID / LINK</td>
<td style="font-size: 10pt;">$$_JOB_ID_$$<br /><a href="$$_JOB_URL_$$" target="_blank">$$_JOB_URL_$$</a></td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: #f6e6c5db;">MESSAGE</td>
<td style="font-size: 10pt;">$$_MESSAGE_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: #f6e6c5db;">ATTACHMENTS</td>
<td style="font-size: 10pt;">$$_ATTACHMENTS_$$</td>
</tr>
<tr style="height: 16px;">
<td style="width: 160px; font-size: 10pt; font-weight:600; background-color: #f6e6c5db;">DESIGN TYPE</td>
<td style="font-size: 10pt;">$$_DESIGN_SUMMARY_$$</td>
</tr>
</tbody>
</table>
<p><span style="color: #999999;"><em>This email was sent from a no-reply email address, however this email has been CC'd to the sender, so you may "Reply All' to communicate directly via email with the sender.</em></span></p>`;
};
